import React, { useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import {
    Box,
    TextField,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    CircularProgress,
    OutlinedInput,
    Checkbox,
    ListItemText,
    FormHelperText,
} from '@mui/material';

import {
    projectIdAtom,
    destinationIdAtom,
    lotIdAtom,
    phaseIdAtom,
    unitIdAtom,
    calculationIdAtom,
    newConfigAtom,
    projectPayloadAtom,
    type Config,
} from 'atoms/Rtool/newConfigAtoms';
import { errorAtom } from 'atoms/error';
import { submitProject } from 'api/submitRtool';
import { toUserMessage } from 'utils/apiErrorMessage';
import { useSnackbar } from 'utils/hooks/useSnackbar';
import { authTokenAtom } from 'atoms/authAtoms';
import { powertrainAtom, type Powertrain } from 'atoms/powertrainAtom';
import type { CalculationSetData } from 'api/fetchRtool';
import { buildCalcIdentityKey } from 'utils/calcIdentity';
import { ReferenceCopyDialog } from './subcomponents/ReferenceCopyDialog';

const alphanumericFilter = /[^\x21-\x7E]|#/g;
const japaneseFilter = /[^\x21-\x22\x24-\x7E\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]/g;

export const NewProject: React.FC = () => {
    const authToken = useAtomValue(authTokenAtom);

    const [projectId, setProjectId] = useAtom(projectIdAtom);
    const [destinationId, setDestinationId] = useAtom(destinationIdAtom);
    const [lotId, setLotId] = useAtom(lotIdAtom);
    const [phaseId, setPhaseId] = useAtom(phaseIdAtom);
    const [unitId, setUnitId] = useAtom(unitIdAtom);
    const [calculationId, setCalculationId] = useAtom(calculationIdAtom);
    const config = useAtomValue(newConfigAtom);

    const [payload, setPayload] = useAtom(projectPayloadAtom);
    const [powertrain, setPowertrain] = useAtom(powertrainAtom);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useAtom(errorAtom);
    const { showSuccess } = useSnackbar();
    const [submitted, setSubmitted] = useState(false);
    const [refOpen, setRefOpen] = useState(false);
    const [referencedKey, setReferencedKey] = useState<string | null>(null);

    const isFormValid =
        projectId.trim() !== '' &&
        destinationId.trim() !== '' &&
        lotId.trim() !== '' &&
        phaseId.trim() !== '' &&
        calculationId.trim() !== '' &&
        unitId.trim() !== '' &&
        payload.mtg != null &&
        payload.description.trim() !== '';

    const handleCreate = async () => {
        setSubmitted(true);
        if (!isFormValid) return;
        setLoading(true);
        setError(null);
        try {
            await submitProject(authToken, config, payload, powertrain);
            showSuccess('計算セットを作成しました。');
        } catch (e) {
            setError(toUserMessage(e));
        } finally {
            setLoading(false);
        }
    };

    // 参照元を選んでフォームへ仮入力する。完全一致のまま作成すると 409 になるため、
    // 反映時の identity key を覚えておき、現在の入力と一致する間は作成を抑止する。
    const applyReference = (refConfig: Config, data: CalculationSetData) => {
        setProjectId(refConfig.project_id);
        setDestinationId(refConfig.destination_id);
        setLotId(refConfig.lot_id);
        setPhaseId(refConfig.phase_id);
        setUnitId(refConfig.unit_id);
        setCalculationId(refConfig.calculation_id);
        setPayload({
            description: data.description,
            mtg: data.mtg,
            correction_direction: data.correction_direction,
        });
        setReferencedKey(buildCalcIdentityKey(powertrain, refConfig));
        setSubmitted(false);
        setError(null);
    };

    const currentKey = buildCalcIdentityKey(powertrain, {
        project_id: projectId,
        destination_id: destinationId,
        lot_id: lotId,
        phase_id: phaseId,
        unit_id: unitId,
        calculation_id: calculationId,
    });
    const isDuplicate = referencedKey !== null && currentKey === referencedKey;

    const fields = [
        {
            label: 'Project ID',
            value: projectId,
            onChange: (v: string) => setProjectId(v.replace(alphanumericFilter, '')),
        },
        {
            label: 'Destination ID',
            value: destinationId,
            onChange: (v: string) => setDestinationId(v.replace(alphanumericFilter, '')),
        },
        {
            label: 'Lot ID',
            value: lotId,
            onChange: (v: string) => setLotId(v.replace(alphanumericFilter, '')),
        },
        {
            label: 'Phase ID',
            value: phaseId,
            onChange: (v: string) => setPhaseId(v.replace(alphanumericFilter, '')),
        },
        {
            label: 'Calculation ID',
            value: calculationId,
            onChange: (v: string) => setCalculationId(v.replace(alphanumericFilter, '')),
        },
    ];

    const mountOptions = [3, 4];
    const correctionOptions = ['X', 'Y', 'Z'];

    return (
        <Box>
            <Typography variant="h5" sx={{ mb: 2 }}>
                R 検討ツール 新規計算セット作成
            </Typography>
            <Box sx={{ mb: 2 }}>
                <Button variant="outlined" onClick={() => setRefOpen(true)}>
                    既存計算セットから複製
                </Button>
            </Box>
            <Box component="form" sx={{ p: 2 }} noValidate autoComplete="off">
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {/* Powertrain（BEV/HEV）— 計算セット単位で作成時に選択する */}
                    <Box sx={{ flex: '1 1 100%' }}>
                        <FormControl fullWidth>
                            <InputLabel id="powertrain-select-label">Powertrain</InputLabel>
                            <Select
                                labelId="powertrain-select-label"
                                value={powertrain}
                                label="Powertrain"
                                onChange={e => setPowertrain(e.target.value as Powertrain)}
                            >
                                <MenuItem value="bev">BEV</MenuItem>
                                <MenuItem value="hev">HEV</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    {fields.map(({ label, value, onChange }) => {
                        const hasError = submitted && value.trim() === '';
                        return (
                            <Box key={label} sx={{ flex: '1 1 calc(50% - 16px)' }}>
                                <TextField
                                    label={label}
                                    fullWidth
                                    value={value}
                                    onChange={e => onChange(e.target.value)}
                                    error={hasError}
                                    helperText={hasError ? '必須項目です' : undefined}
                                />
                            </Box>
                        );
                    })}

                    {/* Unit ID */}
                    <Box sx={{ flex: '1 1 calc(50% - 16px)' }}>
                        <FormControl fullWidth error={submitted && unitId.trim() === ''}>
                            <InputLabel id="unit-select-label">Unit ID</InputLabel>
                            <Select
                                labelId="unit-select-label"
                                value={unitId}
                                label="Unit ID"
                                onChange={e => setUnitId(e.target.value as string)}
                            >
                                <MenuItem value="awd_f">AWD Fr</MenuItem>
                                <MenuItem value="awd_r">AWD Rr</MenuItem>
                                <MenuItem value="fwd">FWD Fr</MenuItem>
                            </Select>
                            {submitted && unitId.trim() === '' && (
                                <FormHelperText>必須項目です</FormHelperText>
                            )}
                        </FormControl>
                    </Box>

                    {/* Description（日本語のみ許可） */}
                    <Box sx={{ flex: '1 1 100%' }}>
                        <TextField
                            label="Description"
                            fullWidth
                            multiline
                            rows={3}
                            value={payload.description}
                            onChange={e =>
                                setPayload({
                                    ...payload,
                                    description: e.target.value.replace(japaneseFilter, ''),
                                })
                            }
                            error={submitted && payload.description.trim() === ''}
                            helperText={
                                submitted && payload.description.trim() === ''
                                    ? '必須項目です'
                                    : undefined
                            }
                        />
                    </Box>

                    {/* Mount Count */}
                    <Box sx={{ flex: '1 1 calc(50% - 16px)' }}>
                        <FormControl fullWidth error={submitted && payload.mtg == null}>
                            <InputLabel id="mtg-select-label">Mount Count</InputLabel>
                            <Select
                                labelId="mtg-select-label"
                                value={payload.mtg ?? ''}
                                label="Mount Count"
                                onChange={e =>
                                    setPayload({ ...payload, mtg: Number(e.target.value) })
                                }
                            >
                                {mountOptions.map(mtg => (
                                    <MenuItem key={mtg} value={mtg}>
                                        {mtg}
                                    </MenuItem>
                                ))}
                            </Select>
                            {submitted && payload.mtg == null && (
                                <FormHelperText>必須項目です</FormHelperText>
                            )}
                        </FormControl>
                    </Box>

                    {/* Correction Direction */}
                    <Box sx={{ flex: '1 1 calc(50% - 16px)' }}>
                        <FormControl fullWidth>
                            <InputLabel id="correction-direction-label">
                                Correction Direction
                            </InputLabel>
                            <Select
                                labelId="correction-direction-label"
                                multiple
                                value={payload.correction_direction}
                                onChange={e =>
                                    setPayload({
                                        ...payload,
                                        correction_direction: e.target.value as string[],
                                    })
                                }
                                input={<OutlinedInput label="Correction Direction" />}
                                renderValue={selected => (selected as string[]).join(', ')}
                            >
                                {correctionOptions.map(option => (
                                    <MenuItem key={option} value={option}>
                                        <Checkbox
                                            checked={
                                                payload.correction_direction.includes(option)
                                            }
                                        />
                                        <ListItemText primary={option} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>

                <Box sx={{ mt: 3 }}>
                    <Button
                        variant="contained"
                        onClick={handleCreate}
                        disabled={loading || isDuplicate}
                    >
                        {loading ? <CircularProgress size={24} /> : '計算セットを作成'}
                    </Button>
                    {isDuplicate && (
                        <Typography color="error" sx={{ mt: 1 }}>
                            参照元と同一の計算セットです。いずれかの ID を変更してください。
                        </Typography>
                    )}
                    {submitted && !isFormValid && (
                        <Typography color="error" sx={{ mt: 1 }}>
                            入力されていない必須項目があります
                        </Typography>
                    )}
                    {error && (
                        <Typography color="error" sx={{ mt: 1 }}>
                            {error}
                        </Typography>
                    )}
                </Box>
            </Box>

            <ReferenceCopyDialog
                open={refOpen}
                onClose={() => setRefOpen(false)}
                onApply={applyReference}
            />
        </Box>
    );
};
